import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import { useNavigate } from "react-router";
import { squarify, type Laid } from "../../lib/squarify";
import {
  buildQueue,
  cardKey,
  reduce,
  useSessionState,
  type CardState,
} from "../../hooks/use-session-state";
import { ResponseId } from "@bindery/shared/contracts/fluency";
import { TreatmentId } from "@bindery/shared/contracts/learning";
import { useFluencyDispatcher } from "../../hooks/use-fluency-dispatcher";
import type { TreemapEntry, SentenceWithPost } from "./types";
import { Tile } from "./tile";
import { LiftedCard } from "./lifted-card";
import { CoverageMeter } from "./coverage-meter";
import { ScaleBar } from "./scale-bar";
import { SaturationBanner } from "./saturation-banner";

type WeightedEntry = TreemapEntry & { weight: number };
type LaidEntry = Laid<WeightedEntry>;

interface Props {
  batchId: string;
  mapNumber: number;
  entries: TreemapEntry[];
  saturationRatio: number;
  /** Server Action: fetch top-scoring sentences for a lemma against a territory. */
  fetchSentences: (
    dictionary_form: string,
    territory: string[],
    medianFreq: number,
  ) => Promise<SentenceWithPost[]>;
  /** Sentences preloaded server-side, keyed by dictionary_form. */
  preloadedSentences: Record<string, SentenceWithPost[]>;
  onDrawNewMap?: () => void;
}

export function TerritoryMap({
  batchId,
  mapNumber,
  entries,
  saturationRatio,
  fetchSentences,
  preloadedSentences,
  onDrawNewMap,
}: Props) {
  const session = useSessionState(batchId, entries);
  const fluency = useFluencyDispatcher({
    sessionId: batchId,
    treatmentId: TreatmentId.PopoverMatch,
    capStrongPromotion: true,
  });
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();
  const sessionStartedAt = useRef<number>(0);
  const [sentenceCache, setSentenceCache] =
    useState<Record<string, SentenceWithPost[]>>(preloadedSentences);
  const [washTokens, setWashTokens] = useState<
    Record<string, { token: number; mark: "known" | "unknown" }>
  >({});
  const [durationMin, setDurationMin] = useState(0);

  useEffect(() => {
    if (sessionStartedAt.current === 0) sessionStartedAt.current = Date.now();
  }, []);

  // Territory = dictionary_forms visible on the map; median = the middle
  // lemma_count among those entries. Both are stable for this component's
  // lifetime, so derive once and pass into every fetchSentences call.
  const { territory, medianFreq } = useMemo(() => {
    const territory = entries.map((e) => e.dictionary_form);
    const counts = entries
      .map((e) => Number(e.lemma_count))
      .sort((a, b) => a - b);
    let mid = 0;
    if (counts.length > 0) {
      mid =
        counts.length % 2 === 1
          ? counts[(counts.length - 1) >> 1]!
          : (counts[counts.length / 2 - 1]! + counts[counts.length / 2]!) / 2;
    }
    return { territory, medianFreq: mid };
  }, [entries]);

  // Aspect-aware layout. The treemap algorithm runs against the actual
  // pixel aspect of the container so squarification stays well-shaped
  // across viewport sizes.
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [aspect, setAspect] = useState({ w: 100, h: 60 });
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => {
      if (!e) return;
      const { width, height } = e.contentRect;
      if (width > 0 && height > 0) {
        // Normalize so w*h = 6000 (matches the 100x60 default scale),
        // preserving the actual aspect ratio.
        const ratio = width / height;
        const h = Math.sqrt(6000 / ratio);
        const w = h * ratio;
        setAspect({ w, h });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const laid = useMemo<LaidEntry[]>(() => {
    if (entries.length === 0) return [];
    const total = entries.reduce(
      (s, e) => s + Math.max(0, Number(e.lemma_count)),
      0,
    );
    // Floor every weight at 0.5% of total so even zero-corpus entries
    // are tappable parcels rather than slivers.
    const floor = Math.max(1, total * 0.005);
    const weighted: WeightedEntry[] = entries.map((e) => ({
      ...e,
      weight: Math.max(floor, Number(e.lemma_count)),
    }));
    const sumWeight = weighted.reduce((s, e) => s + e.weight, 0);
    const out = squarify(weighted, aspect);
    // Convert from container-units to percentages of the bounding box.
    return out.map((r) => ({
      ...r,
      x: (r.x / aspect.w) * 100,
      y: (r.y / aspect.h) * 100,
      w: (r.w / aspect.w) * 100,
      h: (r.h / aspect.h) * 100,
      // Stash normalized weight for percentage display; not used by tile.
      weight: r.weight / sumWeight,
    }));
  }, [entries, aspect]);

  const openEntry = useMemo(() => {
    if (!openKey) return null;
    return laid.find((e) => cardKey(e) === openKey) ?? null;
  }, [openKey, laid]);

  const queue = session.queue;
  const liveQueue = useMemo(
    () => queue.filter((k) => session.state.byKey[k]?.state !== "graduated"),
    [queue, session.state.byKey],
  );

  // Open helper: load sentences if not cached.
  const open = useCallback(
    (key: string) => {
      setOpenKey(key);
      session.setHead(key);
      const cs = session.state.byKey[key];
      if (!cs) return;
      const entry = entries.find(
        (e) =>
          e.jmdict_seq === cs.jmdict_seq &&
          e.kana === cs.kana &&
          e.kanji === cs.kanji,
      );
      if (!entry) return;
      if (entry.dictionary_form in sentenceCache) return;
      startTransition(async () => {
        const rows = await fetchSentences(
          entry.dictionary_form,
          territory,
          medianFreq,
        );
        setSentenceCache((cur) => ({ ...cur, [entry.dictionary_form]: rows }));
      });
    },
    [entries, fetchSentences, session, sentenceCache, territory, medianFreq],
  );

  const advance = useCallback(
    (currentKey: string, mark: "known" | "unknown") => {
      const r = session.review(currentKey, mark);

      const laidEntry = laid.find((e) => cardKey(e) === currentKey);
      if (laidEntry) {
        fluency.dispatch({
          surface: laidEntry.dictionary_form,
          response:
            mark === "known"
              ? ResponseId.Recognition
              : ResponseId.Identification,
          position: {
            kind: "batch-tile",
            jmdictSeq: laidEntry.jmdict_seq,
            rankInBatch: laid.indexOf(laidEntry),
            pos: laidEntry.pos,
          },
        });
      }

      // Run the on-tile wash animation.
      setWashTokens((cur) => ({
        ...cur,
        [currentKey]: { token: (cur[currentKey]?.token ?? 0) + 1, mark },
      }));
      // Compute the next candidate synchronously from the post-review
      // state so it doesn't depend on the closure-captured liveQueue
      // remaining a valid superset across the setTimeout. setState is
      // async — by 280ms we'd otherwise be reading the *pre*-review
      // queue and relying on the "marks only demote/remove" invariant
      // to hold forever.
      const nextState = reduce(
        session.state,
        currentKey,
        mark,
        Date.now(),
      ).next;
      const nextQueue = buildQueue(nextState, entries).filter(
        (k) => nextState.byKey[k]?.state !== "graduated",
      );
      const nextKey = nextQueue.find((k) => k !== currentKey) ?? null;
      setTimeout(() => {
        if (nextKey) {
          open(nextKey);
        } else {
          setOpenKey(null);
        }
      }, 280);
      return r;
    },
    [entries, open, session, laid, fluency],
  );

  const dismiss = useCallback(() => setOpenKey(null), []);

  const navigate = useNavigate();
  const drawNewMap = useCallback(() => {
    session.reset();
    sessionStartedAt.current = Date.now();
    setDurationMin(0);
    if (onDrawNewMap) onDrawNewMap();
    else void navigate(`/n5?map=${mapNumber + 1}`);
  }, [mapNumber, navigate, onDrawNewMap, session]);

  // Update duration only while the saturation banner is on-screen,
  // so most renders don't read Date.now().
  useEffect(() => {
    const ratio = session.fillRatio;
    if (ratio < saturationRatio) return;
    const tick = () => {
      const start = sessionStartedAt.current || Date.now();
      setDurationMin(Math.round((Date.now() - start) / 60000));
    };
    tick();
    const id = setInterval(tick, 30_000);
    return () => clearInterval(id);
  }, [saturationRatio, session.fillRatio]);

  const ratio = session.fillRatio;
  const saturated = ratio >= saturationRatio;
  const graduated = Object.values(session.state.byKey).filter(
    (c) => c.state === "graduated",
  ).length;
  const queueLeft = liveQueue.length;

  const openEntryData = openEntry;
  const openSentences = openEntryData
    ? (sentenceCache[openEntryData.dictionary_form] ?? null)
    : null;
  const openCardState: CardState = openEntryData
    ? (session.state.byKey[cardKey(openEntryData)]?.state ?? "fresh")
    : "fresh";
  const openConsecutive = openEntryData
    ? (session.state.byKey[cardKey(openEntryData)]?.consecutiveKnowns ?? 0)
    : 0;

  return (
    <div className="n5-page relative flex min-h-0 flex-1 flex-col">
      <TopChrome
        mapNumber={mapNumber}
        graduated={graduated}
        total={entries.length}
        ratio={ratio}
        queueLeft={queueLeft}
      />

      <div className="relative min-h-0 flex-1">
        <div
          ref={containerRef}
          className="n5-map absolute inset-0"
          data-lifted={openKey !== null}
        >
          {laid.map((entry, i) => {
            const key = cardKey(entry);
            const cs = session.state.byKey[key];
            const wash = washTokens[key];
            return (
              <Tile
                key={key}
                entry={entry}
                rank={i + 1}
                state={cs?.state ?? "fresh"}
                vacant={openKey === key}
                washToken={wash?.token ?? 0}
                washMark={wash?.mark ?? null}
                onClick={() => open(key)}
              />
            );
          })}
        </div>

        {openKey && openEntryData ? (
          <LiftedOverlay onDismiss={dismiss}>
            <LiftedCard
              entry={openEntryData}
              rank={laid.findIndex((e) => cardKey(e) === openKey) + 1}
              state={openCardState}
              consecutiveKnowns={openConsecutive}
              sentences={openSentences}
              loadingSentences={isPending && openSentences === null}
              emptyQueue={queueLeft <= 1}
              onReview={(mark) => advance(openKey, mark)}
              onDismiss={dismiss}
            />
          </LiftedOverlay>
        ) : null}

        {saturated && !openKey ? (
          <SaturationBanner
            graduated={graduated}
            total={entries.length}
            durationMin={durationMin}
            mapNumber={mapNumber}
            onNext={drawNewMap}
          />
        ) : null}
      </div>

      <ScaleBar />
    </div>
  );
}

function LiftedOverlay({
  children,
  onDismiss,
}: {
  children: React.ReactNode;
  onDismiss: () => void;
}) {
  return (
    // oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- backdrop click-to-dismiss; the keyboard path is Escape, via LiftedCard's useKeyboardReview
    <div
      className="absolute inset-0 z-10 flex items-center justify-center"
      onClick={(e) => {
        if (e.target === e.currentTarget) onDismiss();
      }}
    >
      {/* oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- the scrim, as above: Escape dismisses from the keyboard */}
      <div className="n5-scrim absolute inset-0" onClick={onDismiss} />
      <div className="relative">{children}</div>
    </div>
  );
}

function TopChrome({
  mapNumber,
  graduated,
  total,
  ratio,
  queueLeft,
}: {
  mapNumber: number;
  graduated: number;
  total: number;
  ratio: number;
  queueLeft: number;
}) {
  return (
    <div className="border-rule flex items-center justify-between gap-6 border-b px-4 py-1.5">
      <div className="flex items-baseline gap-3">
        <span
          className="text-base"
          style={{
            fontFamily: "var(--font-jp-display), serif",
            letterSpacing: "0.05em",
            color: "var(--ink)",
          }}
        >
          蔵書
        </span>
        <span className="n5-marginalia">
          N5 Survey · Map № {mapNumber.toString().padStart(2, "0")}
        </span>
      </div>
      <div className="flex items-center gap-3">
        <span className="n5-marginalia">graduated</span>
        <CoverageMeter ratio={ratio} />
        <span className="n5-marginalia">
          {graduated} / {total}
        </span>
        <span className="n5-marginalia hidden sm:inline-block">
          · queue {queueLeft}
        </span>
      </div>
    </div>
  );
}
